import { api } from './client';

export const leaveApi = {
  // === 연차 관리 ===
  getAnnualLeaveAlerts: async (): Promise<any> => {
    const response = await api.get('/api/annual-leave/alerts');
    return response.data;
  },

  getAnnualLeaveSummary: async (year?: number): Promise<any> => {
    const response = await api.get('/api/annual-leave/summary', { params: year ? { year } : {} });
    return response.data;
  },

  getEmployeeAnnualLeave: async (employeeId: string, year?: number): Promise<any> => {
    const response = await api.get(`/api/annual-leave/${encodeURIComponent(employeeId)}`, { params: year ? { year } : {} });
    return response.data;
  },

  useAnnualLeave: async (data: { employee_id: string; days: number; reason: string; year?: number }): Promise<any> => {
    const response = await api.post('/api/annual-leave/use', data);
    return response.data;
  },

  generateAnnualLeaves: async (year: number, excludeExecutives?: boolean): Promise<any> => {
    const response = await api.post('/api/annual-leave/generate', { year, exclude_executives: excludeExecutives ?? true });
    return response.data;
  },

  grantAnnualLeave: async (data: { employee_id: string; year: number; custom_days?: number }): Promise<any> => {
    const response = await api.post('/api/annual-leave/grant', data);
    return response.data;
  },

  getLeaveDashboard: async (year?: number): Promise<any> => {
    const params: any = {};
    if (year) params.year = year;
    const response = await api.get('/api/annual-leave/dashboard', { params });
    return response.data;
  },

  expireAnnualLeaves: async (targetDate?: string): Promise<any> => {
    const params: any = {};
    if (targetDate) params.target_date = targetDate;
    const response = await api.post('/api/annual-leave/expire', null, { params });
    return response.data;
  },

  getUnusedAllowance: async (employeeId: string, year?: number): Promise<any> => {
    const params: any = {};
    if (year) params.year = year;
    const response = await api.get(`/api/annual-leave/unused-allowance/${employeeId}`, { params });
    return response.data;
  },

  cancelAnnualLeave: async (transactionId: number, cancelReason: string): Promise<any> => {
    const response = await api.post('/api/annual-leave/cancel', { transaction_id: transactionId, cancel_reason: cancelReason });
    return response.data;
  },

  // === 특별휴가 ===
  getSpecialLeaveTypes: async (): Promise<any> => {
    const response = await api.get('/api/annual-leave/special-leave/types');
    return response.data;
  },

  getSpecialLeaves: async (params?: any): Promise<any> => {
    const response = await api.get('/api/annual-leave/special-leave', { params });
    return response.data;
  },

  grantSpecialLeave: async (data: any): Promise<any> => {
    const response = await api.post('/api/annual-leave/special-leave/grant', data);
    return response.data;
  },

  useSpecialLeave: async (data: any): Promise<any> => {
    const response = await api.post('/api/annual-leave/special-leave/use', data);
    return response.data;
  },

  // === 육아휴직/출산휴가 ===
  getParentalLeaveSummary: async (): Promise<any> => {
    const response = await api.get('/api/parental-leave/summary');
    return response.data;
  },

  getParentalLeaveAlerts: async (): Promise<any> => {
    const response = await api.get('/api/parental-leave/alerts');
    return response.data;
  },

  getParentalLeaveRecords: async (status?: string): Promise<any> => {
    const response = await api.get('/api/parental-leave/records', { params: status ? { status } : {} });
    return response.data;
  },

  getEmployeeParentalLeave: async (employeeId: string): Promise<any> => {
    const response = await api.get(`/api/parental-leave/${encodeURIComponent(employeeId)}`);
    return response.data;
  },

  createParentalLeave: async (data: { employee_id: string; leave_type: string; start_date: string; end_date: string; child_birth_date?: string; child_name?: string; notes?: string }): Promise<any> => {
    const response = await api.post('/api/parental-leave/', data);
    return response.data;
  },

  updateParentalLeave: async (recordId: number, data: { status?: string; start_date?: string; end_date?: string; notes?: string }): Promise<any> => {
    const response = await api.put(`/api/parental-leave/${recordId}`, data);
    return response.data;
  },

  // === 초과근무 모니터링 ===
  getOvertimeRealtimeWarnings: async (threshold?: number): Promise<any> => {
    const response = await api.get('/api/overtime/realtime-warnings', { params: threshold ? { threshold } : {} });
    return response.data;
  },

  getOvertimeRisks: async (weekStart?: string): Promise<any> => {
    const response = await api.get('/api/overtime/risks', { params: weekStart ? { week_start: weekStart } : {} });
    return response.data;
  },

  getOvertimeStats: async (weekStart?: string): Promise<any> => {
    const response = await api.get('/api/overtime/stats', { params: weekStart ? { week_start: weekStart } : {} });
    return response.data;
  },

  recordAttendance: async (data: { employee_id: string; work_date: string; start_time: string; end_time: string; work_type?: string }): Promise<any> => {
    const response = await api.post('/api/overtime/record', data);
    return response.data;
  },

  getOvertimeDashboard: async (): Promise<any> => {
    const response = await api.get('/api/overtime/dashboard');
    return response.data;
  },

  getOvertimeMonthly: async (employeeId?: string, yearMonth?: string): Promise<any> => {
    const params: any = {};
    if (employeeId) params.employee_id = employeeId;
    if (yearMonth) params.year_month = yearMonth;
    const response = await api.get('/api/overtime/monthly', { params });
    return response.data;
  },

  getOvertimeMonthlyTrends: async (employeeId: string, months: number = 6): Promise<any> => {
    const response = await api.get(`/api/overtime/monthly/${employeeId}/trends`, { params: { months } });
    return response.data;
  },

  getOvertimeViolations: async (params?: any): Promise<any> => {
    const response = await api.get('/api/overtime/violations', { params });
    return response.data;
  },

  resolveOvertimeViolation: async (violationId: number, actionTaken: string): Promise<any> => {
    const response = await api.patch(`/api/overtime/violations/${violationId}/resolve`, { action_taken: actionTaken });
    return response.data;
  },

  getSpecialExtensions: async (params?: any): Promise<any> => {
    const response = await api.get('/api/overtime/special-extensions', { params });
    return response.data;
  },

  createSpecialExtension: async (data: any): Promise<any> => {
    const response = await api.post('/api/overtime/special-extensions', data);
    return response.data;
  },

  approveSpecialExtension: async (extId: number): Promise<any> => {
    const response = await api.patch(`/api/overtime/special-extensions/${extId}/approve`);
    return response.data;
  },
};
